// Code generated by scripts/generate-go-storage. DO NOT EDIT.
//
// This file contains TypeScript wrapper utilities and generated functions
// for the Go WASM storage layer. These handle JSON serialization and error handling.
//
// To regenerate: just generate-go-storage

let initialized = false

export interface GoStorageResult<T = unknown> {
  ok: boolean
  error?: string
  message?: string
  data?: T
}

/**
 * Set initialization state (called by go-storage.ts after WASM init)
 */
export function setInitialized(value: boolean): void {
  initialized = value
}

/**
 * Check if storage is initialized
 */
export function isStorageInitialized(): boolean {
  return initialized
}

/**
 * Parse Go WASM result - all Go functions return JSON strings.
 */
export function parseGoResult<T>(result: string, operation?: string): GoStorageResult<T> & T {
  try {
    return JSON.parse(result)
  } catch {
    const ctx = operation ? `(${operation})` : ''
    return {
      ok: false,
      error: `Failed to parse Go result${ctx}: ${result}`,
    } as GoStorageResult<T> & T
  }
}

/**
 * Helper for calling Go storage functions that return data directly.
 * Handles initialization check, parsing, and error handling.
 * Note: Go WASM wraps responses in {"ok": true, "data": <result>},
 * so we extract .data when present.
 */
export function callGoStorage<T>(
  fn: () => string,
  operation: string
): T {
  if (!initialized) {
    throw new Error('Go storage not initialized')
  }
  const result = parseGoResult<T>(fn(), operation)
  if (!result.ok) {
    throw new Error(result.error || `Failed to ${operation}`)
  }
  // Go WASM wraps responses in {ok, data}, extract .data if present
  // Otherwise return the result directly (for backwards compatibility with inline fields)
  if (result.data !== undefined) {
    return result.data as T
  }
  return result as T
}

/**
 * Helper for calling Go storage functions that return arrays in .data.
 */
export function callGoStorageArray<T>(
  fn: () => string,
  operation: string
): T[] {
  if (!initialized) {
    throw new Error('Go storage not initialized')
  }
  const result = parseGoResult<T[]>(fn(), operation)
  if (!result.ok) {
    throw new Error(result.error || `Failed to ${operation}`)
  }
  return result.data ?? []
}

/**
 * Helper for calling Go storage functions that return a single value.
 */
export function callGoStorageValue<T extends number | string | boolean>(
  fn: () => string,
  operation: string
): T {
  if (!initialized) {
    throw new Error('Go storage not initialized')
  }
  const result = parseGoResult<{ value: T }>(fn(), operation)
  if (!result.ok) {
    throw new Error(result.error || `Failed to ${operation}`)
  }
  return (result as { value: T }).value
}

/**
 * Helper for calling Go storage functions that don't return data.
 */
export function callGoStorageVoid(
  fn: () => string,
  operation: string
): void {
  if (!initialized) {
    throw new Error('Go storage not initialized')
  }
  const result = parseGoResult<void>(fn(), operation)
  if (!result.ok) {
    throw new Error(result.error || `Failed to ${operation}`)
  }
}

